import { apiFetch } from './client.ts';
import type { Session, StrapiListResponse } from './types.ts';

/**
 * Session lifecycle calls ops-dualscreen needs: which session is live, creating one, activating
 * one. Mid-session settings (`songsPerRotation`, `requestMode`, ...) go through the real
 * `PATCH /api/session/current` — `patchSessionCurrent` in `./rotation.ts`, which returns the
 * rotation snapshot that call re-broadcasts (MVP-216: the core-CRUD `PUT /api/sessions/:id`
 * fallback that used to live here is gone).
 */
/**
 * MVP-612 names `venueRef` and `roomRef` on this call and on this call ALONE.
 *
 * The room-move picker needs the venue the night is in to drop another branch's rooms, and the room
 * it is in to exclude the no-op destination; both are relations Strapi omits unless asked for. Named
 * fields in the `venues.ts` idiom — `populate=*` is banned repo-wide, and here it would also drag a
 * whole venue and room row (notes, themes, marquee) onto a poll that runs every few seconds.
 *
 * `createSession` and `activateSession` deliberately do NOT ask: neither result reaches the picker,
 * and a populate on a write response is a wider payload bought for nobody.
 */
export async function getActiveSession(): Promise<Session | null> {
  const res = await apiFetch<StrapiListResponse<Session>>(
    '/api/sessions?filters[isActive][$eq]=true&pagination[pageSize]=1' +
      '&populate[venueRef][fields][0]=documentId' +
      '&populate[roomRef][fields][0]=documentId&populate[roomRef][fields][1]=slug'
  );
  return res.data[0] ?? null;
}

/** MVP-11 #1 — core POST /api/sessions. `joinCode` is generated by the session lifecycle when
 * omitted; `requestMode` defaults to `review` server-side and stays there. */
export async function createSession(input: { name?: string; venue?: string } = {}): Promise<Session> {
  const res = await apiFetch<{ data: Session }>('/api/sessions', { method: 'POST', body: { data: input }, retry: false });
  return res.data;
}

/**
 * One `play-history` row as `GET /api/session/summary` shapes it (SPEC AC-Q-11).
 *
 * `singer` and `song` are the names as they were recorded, not joins done now — a renamed singer
 * or a re-tagged song must not rewrite what the export says happened. The three ids (MVP-414) are
 * how the cockpit's history panel puts a song back in the queue; they are outside the CSV.
 */
export interface SessionSummaryRow {
  historyId: string;
  songId: string | null;
  singerId: string | null;
  startedAt: string;
  endedAt: string;
  singer: string;
  song: string;
  artist: string;
  keyShift: number;
  completed: boolean;
  playedMs: number | '';
  /** `'queue'` is a singer's performance; `'filler'` is the break-music bus, which SPEC keeps out
   * of singer history (see `BreakMusicPanel.tsx`) but inside the session export. */
  source: string;
}

/** `GET /api/session/summary` (SPEC AC-Q-11). MVP-414: written since MVP-249, read by nothing
 * until the cockpit's history panel. Not paginated server-side — the service caps at 5000 rows,
 * which is more performances than a night has. */
export async function getSessionSummary(): Promise<SessionSummaryRow[]> {
  const res = await apiFetch<{ data: SessionSummaryRow[] }>('/api/session/summary');
  return res.data ?? [];
}

/**
 * MVP-498 slice 2 — POST /api/sessions/:documentId/move-room (session controller `moveRoom`).
 *
 * `room` is the target room's DOCUMENT id, not its numeric id: the server resolves it through
 * `roomInScope`, so a room belonging to a venue this account cannot reach answers 404 (never 403 —
 * that would confirm the row exists). The response's `meta` carries the from-room and to-room, which
 * is what the audit row records and what an undo would need when slice 3 comes.
 *
 * `retry: false`, like `activateSession` and `createSession` above and for a sharper reason than
 * either: this call takes two room-row locks inside a transaction, and an automatic retry racing its
 * own first attempt would queue behind locks the first attempt still holds.
 */
export async function moveSessionRoom(
  documentId: string,
  roomDocumentId: string
): Promise<{ session: Session; fromRoom: number | null; toRoom: number | null }> {
  const res = await apiFetch<{ data: Session; meta?: { fromRoom?: number | null; toRoom?: number | null } }>(
    `/api/sessions/${encodeURIComponent(documentId)}/move-room`,
    { method: 'POST', body: { data: { room: roomDocumentId } }, retry: false }
  );
  return { session: res.data, fromRoom: res.meta?.fromRoom ?? null, toRoom: res.meta?.toRoom ?? null };
}

/**
 * MVP-642 — POST /api/sessions/:documentId/rotate-join-code (session controller `rotateJoinCode`).
 *
 * The route, the controller and the KJ grant have existed since MVP-227 and had no caller in
 * `web/src` at all until this ticket: a KJ whose code had leaked — a photographed QR, a printed card
 * left on a table — could only invalidate it by ending the night, which also ends the rotation order,
 * the queue and the play history, because all three hang off `session`.
 *
 * No body. The controller takes nothing but `:id`, mints the new code with the session service's own
 * `generateUniqueJoinCode()` and returns the updated row — the client never proposes a code, which is
 * what keeps uniqueness a server property.
 *
 * `retry: false`, and here that is not the usual "never silently replay a write". A replay would
 * rotate a SECOND time, and the code the first attempt returned is the one the operator is already
 * reading out to the room — so a retry does not repeat a write, it invalidates a code the KJ believes
 * is live. The failure is silent and lands on the guests.
 */
export async function rotateSessionJoinCode(documentId: string): Promise<Session> {
  const res = await apiFetch<{ data: Session }>(
    `/api/sessions/${encodeURIComponent(documentId)}/rotate-join-code`,
    { method: 'POST', body: {}, retry: false }
  );
  return res.data;
}

/** MVP-11 #1 — POST /api/sessions/:documentId/activate (session controller `activate`). */
export async function activateSession(documentId: string): Promise<Session> {
  const res = await apiFetch<{ data: Session }>(`/api/sessions/${documentId}/activate`, { method: 'POST', body: {}, retry: false });
  return res.data;
}

/**
 * MVP-678 — POST /api/session/passcode-check (session controller `passcodeCheck`).
 *
 * "Is this the venue passcode?", asked by the console so storefront mode's exit can be gated by it.
 * Never sends the PIN in a query string (a URL is written to the proxy log and the browser's own
 * history — the reason the guest gate became a POST in MVP-469), and the answer is a verdict only.
 *
 * `required: false` means this night has no passcode at all, and the caller should let the operator
 * straight out: `storefrontMode.ts` fails open on purpose, and a gate with no correct answer would
 * be the exact "console nobody can leave" that module refuses to create.
 *
 * `retry: false`: a replay of a wrong PIN spends a second attempt out of a budget of three.
 */
export async function checkVenuePasscode(passcode?: string | null): Promise<{ required: boolean; ok: boolean }> {
  const res = await apiFetch<{ data: { required: boolean; ok: boolean } }>('/api/session/passcode-check', {
    method: 'POST',
    body: { data: { passcode: passcode ?? null } },
    retry: false,
  });
  return res.data;
}
